import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { usePageMeta } from '../utils/pageMeta';

// Standalone public newsletter signup — the shareable version of the section
// at the bottom of the landing page. Same callable, same rules; this one exists
// so a link can be posted on its own (flyer QR code, social bio, group chat)
// and land on nothing but the form. "/" is not usable for that: signed-in
// visitors get redirected off it to their start page.

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Barlow:wght@300;400;600&display=swap');
  .tbvn-page { all: initial; }
  .tbvn-page *, .tbvn-page *::before, .tbvn-page *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .tbvn-page {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 100vh; background: #0D1E35; color: white; font-family: 'Barlow', sans-serif;
    padding: 40px 16px 48px; overflow-x: hidden; text-align: center;
  }
  .tbvn-header { display: flex; flex-direction: column; align-items: center; margin-bottom: 30px; animation: tbvn-fadeDown 0.7s ease both; }
  .tbvn-card { width: 100%; max-width: 520px; animation: tbvn-fadeUp 0.55s 0.15s ease both; }
  .tbvn-title { font-family: 'Bebas Neue', sans-serif; font-size: clamp(1.6rem,5vw,2.2rem); letter-spacing: 5px; color: #C9A84C; margin-bottom: 10px; }
  .tbvn-sub { font-size: 0.9rem; font-weight: 300; color: rgba(255,255,255,0.6); line-height: 1.6; margin-bottom: 22px; }
  .tbvn-form { display: flex; flex-direction: column; gap: 10px; }
  .tbvn-input {
    width: 100%; padding: 12px 14px; border-radius: 8px;
    background: rgba(255,255,255,0.06); border: 1px solid rgba(201,168,76,0.3);
    color: white; font-family: 'Barlow', sans-serif; font-size: 0.95rem;
  }
  .tbvn-input::placeholder { color: rgba(255,255,255,0.35); }
  .tbvn-input:focus { outline: none; border-color: #C9A84C; }
  .tbvn-btn {
    padding: 12px 22px; border-radius: 8px; border: none; cursor: pointer;
    background: #C9A84C; color: #0D1E35; font-family: 'Barlow', sans-serif;
    font-weight: 600; font-size: 0.9rem; letter-spacing: 1.5px; text-transform: uppercase;
    transition: background 0.2s ease;
  }
  .tbvn-btn:hover:not(:disabled) { background: #e0be6a; }
  .tbvn-btn:disabled { opacity: 0.55; cursor: default; }
  .tbvn-msg { font-size: 0.85rem; margin-top: 14px; line-height: 1.55; }
  .tbvn-ok { color: #7FCB9B; }
  .tbvn-err { color: #E28E82; }
  .tbvn-fine { font-size: 0.72rem; font-weight: 300; color: rgba(255,255,255,0.35); line-height: 1.6; margin-top: 20px; }
  .tbvn-fine a { color: rgba(201,168,76,0.75); text-decoration: none; }
  .tbvn-fine a:hover { color: #C9A84C; }
  .tbvn-footer { margin-top: 34px; font-size: 0.68rem; color: rgba(255,255,255,0.18); letter-spacing: 2px; text-transform: uppercase; }
  .tbvn-footer a { color: rgba(255,255,255,0.25); text-decoration: none; display: block; margin-bottom: 6px; letter-spacing: 2px; }
  .tbvn-footer a:hover { color: rgba(201,168,76,0.6); }
  @keyframes tbvn-fadeDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes tbvn-fadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
`;

function NewsletterSignup() {
  usePageMeta(
    'Club Newsletter | {club}',
    'Sign up for the {club} newsletter — tournaments, leagues, camps and open play at {facility} in Orlando, FL.'
  );

  const [form, setForm] = useState({ name: '', email: '', website: '' });
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);   // { ok, text }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubscribe(e) {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) { setMsg({ ok: false, text: 'Please enter your name.' }); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setMsg({ ok: false, text: 'Please enter a valid email address.' }); return;
    }
    setSending(true); setMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'subscribeNewsletter');
      const r = await fn({ name, email, website: form.website });
      setForm({ name: '', email: '', website: '' });
      // An address that unsubscribed before stays suppressed — the form can't
      // undo that, so say so rather than promise email that will never arrive.
      setMsg(r.data?.optedOut
        ? { ok: false, text: 'That address previously unsubscribed from club email, so we’ve left it off. Email info@torchvolleyball.com to turn it back on.' }
        : { ok: true, text: r.data?.alreadySubscribed ? 'You’re already on the list — nothing to do.' : 'You’re on the list. Watch your inbox for club news.' });
    } catch (err) {
      console.warn('newsletter signup failed:', err);
      setMsg({ ok: false, text: err?.message || 'Something went wrong. Please try again.' });
    }
    setSending(false);
  }

  return (
    <>
      <style>{styles}</style>
      <div className="tbvn-page">
        <header className="tbvn-header">
          <a href="/">
            <img src="/horzlogo.png" alt="Torch Beach Volleyball"
                 style={{ maxWidth: 300, width: '75vw', objectFit: 'contain' }} />
          </a>
          <img src="/goldline1.png" alt=""
               style={{ maxWidth: 340, width: '80vw', objectFit: 'contain', marginTop: 10, opacity: 0.85 }} />
        </header>

        <main className="tbvn-card">
          <h1 className="tbvn-title">Club Newsletter</h1>
          <div className="tbvn-sub">
            Tournaments, leagues, camps and open play — sent occasionally, never shared.
            Every email has an unsubscribe link.
          </div>

          <form className="tbvn-form" onSubmit={handleSubscribe}>
            <input className="tbvn-input" type="text" placeholder="Your name"
                   value={form.name} onChange={set('name')} autoComplete="name" />
            <input className="tbvn-input" type="email" placeholder="Email address"
                   value={form.email} onChange={set('email')} autoComplete="email" />
            {/* Honeypot — hidden from humans, bots fill it and get silently dropped */}
            <input type="text" name="website" value={form.website} onChange={set('website')}
                   style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                   tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <button className="tbvn-btn" type="submit" disabled={sending}>
              {sending ? 'Signing up…' : 'Sign Up'}
            </button>
          </form>

          {msg && (
            <div className={`tbvn-msg ${msg.ok ? 'tbvn-ok' : 'tbvn-err'}`}>{msg.text}</div>
          )}

          <div className="tbvn-fine">
            Already a member? Club news goes to your account address automatically —
            no need to sign up here. Questions:{' '}
            <a href="mailto:info@torchvolleyball.com">info@torchvolleyball.com</a>
          </div>
        </main>

        <footer className="tbvn-footer">
          <a href="/">Torch Beach Volleyball Home</a>
          <a href="/privacy">Privacy Policy</a>
          © {new Date().getFullYear()} Torch Beach Volleyball · Orlando, FL
        </footer>
      </div>
    </>
  );
}

export default NewsletterSignup;
